import { api_configuration } from "../../common";
import DestructorTree from "../DestructorTree";
import { settings } from "../state/ClientState";
import { registerQueryEntry, registerStategroup, registerGroup, registerText } from "../state/registerClientState";
import { ensure, isNonNull } from "../validation";
import { walk_context_ancestor_path } from "./context_selection";
import { ControlProperties, getControl } from "./ControlLibrary";
import ImplementationVisitorContext from "./ImplementationVisitorContext";
import { visit_widget_expression } from "./visit_widget_expression";
import { visitExpression, visitVariable } from "./visit_widget_implementation_expression";
import { visit_widget_implementation_node } from "./visit_widget_implementation_node";
import { wrapNode } from "./wrapNode";
import api_cs = api_configuration.types.client_state.alan_api;
import api_widget = api_configuration.libraries.gui_definition_lib.types.widget.alan_api;
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import ClientState = require("../state/ClientState");
import Window = require("./dom/Window");
import context_selection = require("./context_selection");
import * as SubscriptionManager from "../state 2 interface/SubscriptionManager";
import { decorateControlError, isNonDecoratedError } from "./stackTraceMessage";
import * as renderQueue from "../renderQueue";
import { iReference } from "../state/Reference";
import * as step from "../state/step";
import * as deeplinkHandler from "../front/deeplinkHandler";
import { createExpressionBuilder } from "../../../../../stream";

function createDomElementThatDestructsOnDestructorTreeDestroy(
	dom_element_constructor: () => HTMLElement,
	destructor: DestructorTree
): () => HTMLElement {
	return () => {
		//FIXME: Do not do this for every control initializer.
		//FIXME: rewrite using Proxy?
		const dom_element = dom_element_constructor();
		const patchedElement = Object.create(dom_element) as HTMLElement;
		patchedElement.appendChild = function <T extends Node>(node: T) {
			destructor.onDestroy(function () {
				if (node) {
					(<any>node).remove();
				}
			});
			return dom_element.appendChild(node);
		};
		if (dom_element.insertBefore) {
			patchedElement.insertBefore = function <T extends Node>(node: T, child: Node | null) {
				destructor.onDestroy(function () {
					if (node) {
						(<any>node).remove();
					}
				});
				return dom_element.insertBefore(node, child);
			};
		}
		return patchedElement;
	};
}

function swich_binding_type(
	control_binding: api_widget.Ccontrol_binding,
	implementation_context: ImplementationVisitorContext,
	control_parent_dom_element: () => HTMLElement,
	destructor: DestructorTree,
	wrap_in_dynamic_area: boolean
) {
	switch (control_binding.properties.binding_type.state.name) {
		case "let declaration": {
			const let_declaration__binding_type = control_binding.properties.binding_type.cast("let declaration");

			const new_let_declaration_context = walk_context_ancestor_path(
				let_declaration__binding_type.properties.path,
				implementation_context
			);
			visitExpression(let_declaration__binding_type.properties.declaration.ref.properties.expression).subscribe(
				new_let_declaration_context,
				{
					add: (value: api_widget.Ccontrol_binding, sub_implementation_context, sub_destructor, wrap) => {
						visit_control_binding(
							value,
							control_parent_dom_element,
							sub_implementation_context,
							sub_destructor,
							wrap || wrap_in_dynamic_area
						);
					},
				},
				destructor,
				undefined
			);
			break;
		}
		case "static": {
			const static__binding_type = control_binding.properties.binding_type.cast("static");
			const wrapped_dom_element = wrap_in_dynamic_area
				? wrapNode(control_parent_dom_element, destructor)
				: control_parent_dom_element;
			const control_properties = getControl(
				static__binding_type.properties.control.ref.key!,
				wrapped_dom_element,
				createDomElementThatDestructsOnDestructorTreeDestroy(implementation_context.get_view_root(), destructor),
				makeControlDestructor(destructor)
			);
			visit_widget_implementation_node(
				static__binding_type.properties.node_binding,
				control_properties as ControlProperties,
				implementation_context,
				destructor
			);
			break;
		}
		case "window": {
			const window__binding_type = control_binding.properties.binding_type.cast("window");
			const window_widget_name = window__binding_type.properties.window.entry;
			const window_definition = implementation_context.gui_widget_configuration.properties.configuration
				.get(window_widget_name)!
				.properties.attribute.properties.type.cast("window");
			const window_name = window_definition.properties.window.ref.key;
			const cs_window = ClientState.client_state.properties.windows.create(window_name, {
				"has active view": ["no", {}],
				"open views": {},
			});

			let window_div: HTMLElement;
			const window_div_constructor = () => {
				if (window_div === undefined) {
					window_div = isNonNull(control_parent_dom_element().ownerDocument).createElementNS(
						"http://www.w3.org/1999/xhtml",
						"div"
					);
					window_div.classList.add("engine-window-list");
					const wrapped_dom_element = wrap_in_dynamic_area
						? wrapNode(control_parent_dom_element, destructor)
						: control_parent_dom_element;
					wrapped_dom_element().appendChild(window_div);
					destructor.onDestroy(() => {
						window_div.remove();
					});
				}
				return window_div;
			};

			Window.initialize(window_name, window_div_constructor, destructor, cs_window);
			settings.properties.allow_deeplink.switch({
				yes: function ($) {
					const target_window = $.properties.target_window.ref;
					if (window_name === target_window.key) {
						deeplinkHandler.startListening(target_window);
						destructor.onDestroy(deeplinkHandler.stopListening);
					}
				},
				no: undefined,
			});

			const gui_widget_config__window = implementation_context.gui_widget_configuration.properties.configuration
				.get(window__binding_type.properties.window.entry)!
				.properties.attribute.properties.type.cast("window");
			const variable_context = visitVariable(
				window__binding_type.properties.variable,
				implementation_context.switchGuiWidgetConfiguration(gui_widget_config__window.properties.configuration),
				destructor
			);
			visit_control_binding(
				window__binding_type.properties.control_binding,
				window_div_constructor,
				variable_context,
				destructor,
				wrap_in_dynamic_area
			);
			break;
		}
		case "widget": {
			const widget__binding_type = control_binding.properties.binding_type.cast("widget");
			const selected_implementation_context = context_selection.context_selection(
				widget__binding_type.properties.context,
				implementation_context,
				destructor
			);
			const gui_widget_config = selected_implementation_context.gui_widget_configuration.properties.configuration
				.get(widget__binding_type.properties.widget.entry)!
				.properties.attribute.properties.type.cast("widget");
			const wrapped_dom_element = wrap_in_dynamic_area
				? wrapNode(control_parent_dom_element, destructor)
				: control_parent_dom_element;
			visit_widget_expression(
				gui_widget_config.properties.instance,
				selected_implementation_context.current_binding_context,
				destructor,
				{
					model_binding: selected_implementation_context.raw_model_binding,
					parent_dom_element: wrapped_dom_element,
					client_view: selected_implementation_context.get_client_view(),
					view_root_dom_element: selected_implementation_context.get_view_root(),
					entry_keys: selected_implementation_context.entry_keys,
				},
				wrap_in_dynamic_area
			);
			break;
		}
		case "client binding widget": {
			const client_binding_widget_type = control_binding.properties.binding_type.state.node;
			const selected_implementation_context = context_selection.context_selection(
				client_binding_widget_type.properties.context,
				implementation_context,
				destructor
			);
			const widget_cell = ensure(selected_implementation_context.current_binding_context, api_cs.Cwidget__type__cells);
			const entry = widget_cell.parent.parent;
			const node = registerQueryEntry(entry.properties.entry_status, destructor);

			switch (node.properties.location.state.name) {
				case "remote":
					SubscriptionManager.finishedAndInitialized().then(() =>
						stageRenderCell(widget_cell, node, control_parent_dom_element, selected_implementation_context, destructor)
					);
					break;
				case "local":
					stageRenderCell(widget_cell, node, control_parent_dom_element, selected_implementation_context, destructor);
					break;
				default:
					unexpectedState(node.properties.location.state);
			}
			break;
		}
		default:
			unexpectedState(control_binding.properties.binding_type.state);
	}
}
export function visit_control_binding(
	control_binding: api_widget.Ccontrol_binding,
	parent_dom_element: () => HTMLElement,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree,
	wrap_in_dynamic_area: boolean
): void {
	const control_destructor = destructor.addChild();
	try {
		swich_binding_type(
			control_binding,
			implementation_context,
			parent_dom_element,
			control_destructor,
			wrap_in_dynamic_area
		);
	} catch (e) {
		if (isNonDecoratedError(e)) {
			throw decorateControlError(e, control_binding, implementation_context);
		}
		throw e;
	}
}

function subscribeToNodePath(
	path: api_views.Cnode_path,
	node: api_cs.Cnode,
	destructor: DestructorTree,
	callback: (node: api_cs.Cnode | undefined, destructor: DestructorTree) => void
): void {
	switch (path.properties.has_steps.state.name) {
		case "no":
			callback(node, destructor);
			break;
		case "yes": {
			const has_steps = path.properties.has_steps.state.node;
			switch (has_steps.properties.type.state.name) {
				case "parent":
					subscribeToNodePath(
						has_steps.properties.tail,
						node.output.position().output.parent().cast("node"),
						destructor,
						callback
					);
					break;
				case "state rule":
					callback(undefined, destructor);
					break;
				case "reference rule":
					callback(undefined, destructor);
					break;
				case "reference": {
					const reference_step = has_steps.properties.type.state.node;
					const reference = registerText(
						reference_step.properties.text.ref.inferences.text(),
						node,
						destructor
					).properties.navigable.cast("yes");
					const reference_expression = createExpressionBuilder(iReference(reference).referencedNode).pipe(
						step.sync((node, destructor) => subscribeToNodePath(has_steps.properties.tail, node, destructor, callback))
					)();
					const subscription = reference_expression.subscribe({ update: () => undefined });
					destructor.onDestroy(() => reference_expression.unsubscribe(subscription));
					break;
				}
				case "group": {
					const group_step = has_steps.properties.type.state.node;
					const type_node = registerGroup(group_step.properties.group.ref.inferences.group(), node, destructor)
						.properties.node;
					subscribeToNodePath(has_steps.properties.tail, type_node, destructor, callback);
					break;
				}
				case "state": {
					const state_type = has_steps.properties.type.state.node;
					const state_group = registerStategroup(
						state_type.properties.state_group.ref.inferences.state_group(),
						node,
						destructor
					);
					const state = state_group.properties.states.get(state_type.properties.state.entry)!;
					destructor.subscribeToStategroup(state.properties.is_selected, (destructor) => {
						switch (state.properties.is_selected.state.name) {
							case "no":
								callback(undefined, destructor);
								break;
							case "yes":
								subscribeToNodePath(has_steps.properties.tail, state.properties.node, destructor, callback);
								break;
						}
					});
					break;
				}
				case "collection entry":
					callback(undefined, destructor);
					break;
			}
			break;
		}
	}
}

function makeControlDestructor(destructor: DestructorTree) {
	return (callback: () => void) => {
		destructor.onDestroy(() => {
			callback();
		});
	};
}

function stageRenderCell(
	widget_cell: api_cs.Cwidget__type__cells,
	node: api_cs.Cnode,
	control_parent_dom_element: () => HTMLElement,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree
) {
	const task = renderQueue.add(() => {
		const property_path = widget_cell.inferences.column_type().inferences.widget_type().parent.properties.path;
		const widget_instance = widget_cell.inferences.column_type().inferences.widget_type().properties.instance;
		subscribeToNodePath(property_path, node, destructor, (selected_node, destructor) => {
			if (selected_node !== undefined) {
				visit_widget_expression(
					widget_instance,
					selected_node,
					destructor,
					{
						client_view: implementation_context.get_client_view(),
						entry_keys: implementation_context.entry_keys,
						model_binding: implementation_context.raw_model_binding,
						parent_dom_element: wrapNode(control_parent_dom_element, destructor),
						view_root_dom_element: implementation_context.get_view_root(),
					},
					false
				);
			}
		});
	});
	destructor.onDestroy(() => {
		renderQueue.remove(task);
	});
}
